import { React, useEffect, useRef, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import MembershipAnalyticsTable from "./MembershipAnalyticsTable";
import MembershipAnalyticsCharts from "./MembershipAnalyticsCharts";
import MembershipAnalyticsSummary from "./MembershipAnalyticsSummary";
import { COLORS } from "../../utils/colors";
import { api } from "../../utils/api";
import usePriceChanges from "../../hooks/usePriceChanges";
import EmptyState from "../EmptyStates/EmptyState";

const FILTERS_STORAGE_KEY = "membershipAnalyticsFilters";
const REFRESH_POLL_MS = 3000;
// About five minutes of polling; a full rebuild normally takes under a minute.
const REFRESH_POLL_LIMIT = 100;

function formatRefreshTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function refreshStatusText(status) {
  if (!status) return "";
  if (status.running) return "Updating figures with the latest data…";
  if (status.state === "failed") return "Couldn't update figures. Showing the last saved numbers.";
  if (status.state === "done" && status.finishedAt) {
    return `Figures updated ${formatRefreshTime(status.finishedAt)}`;
  }
  return "";
}
const ANY_PRESET = { value: "", label: "Any" };
const METRIC_PRESETS = {
  monthsActive: [
    ANY_PRESET,
    { value: "lt:1", label: "Under 1 month" },
    { value: "gte:1", label: "1+ months" },
    { value: "gte:2", label: "2+ months" },
    { value: "gte:3", label: "3+ months" },
    { value: "gte:4", label: "4+ months" },
    { value: "gte:5", label: "5+ months" },
    { value: "gte:6", label: "6+ months" },
    { value: "gte:12", label: "12+ months" },
  ],
  monthlyRedemptionRate: [
    ANY_PRESET,
    { value: "lt:1", label: "Under 1 / month" },
    { value: "gte:1", label: "1+ / month" },
    { value: "gte:2", label: "2+ / month" },
    { value: "gte:4", label: "4+ / month" },
  ],
  monthlyProfit: [
    ANY_PRESET,
    { value: "lt:0", label: "Losing" },
    { value: "eq:0", label: "Breakeven" },
    { value: "gt:0", label: "Profitable" },
    { value: "gte:20", label: "$20+ / month" },
    { value: "gte:50", label: "$50+ / month" },
  ],
};
const NONE_LEAD_SOURCE = "__none__";
const NONE_SOURCE_OPTION = {
  group: "Other",
  label: "None",
  value: NONE_LEAD_SOURCE,
};

function sourceOptionFromName(group, name) {
  const label = String(name || "").trim();
  if (!label || label === NONE_LEAD_SOURCE) return null;
  return { group: group || "Source", label, value: label };
}

function buildSourceOptions(data) {
  const groups = data && data.groups;
  const options = [];
  if (Array.isArray(groups) && groups.length) {
    groups.forEach((group) => {
      (group.sources || []).forEach((name) => {
        const option = sourceOptionFromName(group.group, name);
        if (option) options.push(option);
      });
    });
  } else {
    ((data && data.sources) || []).forEach((name) => {
      const option = sourceOptionFromName("Source", name);
      if (option) options.push(option);
    });
  }

  const rest = options.filter((option) => option.value !== NONE_LEAD_SOURCE);
  const lastOtherIndex = rest.reduce(
    (last, option, index) => (option.group === "Other" ? index : last),
    -1
  );
  if (lastOtherIndex === -1 || lastOtherIndex === rest.length - 1) {
    return [...rest, NONE_SOURCE_OPTION];
  }
  const next = rest.slice();
  next.splice(lastOtherIndex + 1, 0, NONE_SOURCE_OPTION);
  return next;
}

const COLUMN_FILTER_IDS = [
  "paymentStatus",
  "monthsActive",
  "monthlyRedemptionRate",
  "monthlyProfit",
  "accountActivationStatus",
  "priceHistory",
];
// "all" shows in the field but is never sent, so the backend applies no filter.
const ALL_PRICE_HISTORY = "all";
const PRICE_HISTORY_PRESETS = [
  { value: ALL_PRICE_HISTORY, label: "All" },
  { value: "with", label: "Price updated" },
  { value: "without", label: "No price update" },
];
// Matches paymentStatus on each row, worked out from our subscription record.
const SUBSCRIPTION_STATUS_PRESETS = [
  ANY_PRESET,
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "cancelled", label: "Cancelled" },
];
// The page opens on active members unless this tab already picked another status.
const DEFAULT_SUBSCRIPTION_STATUS = "active";
const ACTIVATION_PRESETS = [
  ANY_PRESET,
  { value: "activated", label: "Activated" },
  { value: "not_activated", label: "Not activated" },
];

function emptyColumnFilters() {
  return COLUMN_FILTER_IDS.reduce((filters, id) => {
    filters[id] = "";
    return filters;
  }, {});
}

function initialColumnFilters(saved) {
  const next = {
    ...emptyColumnFilters(),
    paymentStatus: DEFAULT_SUBSCRIPTION_STATUS,
    priceHistory: ALL_PRICE_HISTORY,
  };
  const stored = saved && saved.columnFilters;
  if (!stored || typeof stored !== "object") return next;
  COLUMN_FILTER_IDS.forEach((id) => {
    if (stored[id] != null) next[id] = String(stored[id]);
  });
  return next;
}

function appliedColumnFilters(filters) {
  const next = {};
  COLUMN_FILTER_IDS.forEach((id) => {
    const value = String((filters && filters[id]) || "").trim();
    if (!value) return;
    if (id === "priceHistory" && value === ALL_PRICE_HISTORY) return;
    next[id] = value;
  });
  return next;
}

function hasActiveAnalyticsFilters(filters) {
  if (filters.product || filters.leadSource) return true;
  if (String(filters.keyword || "").trim().length > 2) return true;
  return Object.keys(appliedColumnFilters(filters.columnFilters)).length > 0;
}

function loadSavedFilters() {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function MetricPresetField({ id, label, value, options, onChange }) {
  const labelId = `${id}-label`;
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={id}
        label={label}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={`${id}-${option.value || "any"}`} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const MembershipAnalytics = () => {
  const savedFilters = loadSavedFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState(savedFilters.keyword || "");
  const [product, setProduct] = useState(savedFilters.product || "");
  const [leadSource, setLeadSource] = useState(savedFilters.leadSource || "");
  const [sourceOptions, setSourceOptions] = useState([NONE_SOURCE_OPTION]);
  const [columnFilters, setColumnFilters] = useState(() => initialColumnFilters(savedFilters));
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [summary, setSummary] = useState(null);
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || "startDateUTC");
  const [sortDir, setSortDir] = useState(savedFilters.sortDir || "desc");
  const pageSize = 100;
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const mountedRef = useRef(true);
  // The rebuild finishes after later renders, so reload with the filters on screen then.
  const reloadRowsRef = useRef(null);
  const priceChanges = usePriceChanges(
    "subscription",
    rows.map((row) => row.subscription?._id || row.subscription)
  );

  const fetchLeadSources = async () => {
    try {
      const response = await api("v1/membership-analytics/lead-sources", "GET");
      if (response.data.success) {
        setSourceOptions(buildSourceOptions(response.data.data || {}));
      }
    } catch (error) {
      console.error("Failed to load lead sources", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api("v1/product?includeAll=true", "GET");
      if (response.data.success) {
        const list = (response.data.data.allProducts || []).filter(
          (p) => p.type === "subscription" && p.isAddOnOnly !== true
        );
        setProducts(list);
        if (product && !list.some((p) => p._id === product)) {
          setProduct("");
        }
      }
    } catch (error) {
      console.error("Failed to load products", error);
    }
  };

  const fetchRows = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api("v1/membership-analytics/search", "POST", {
        product: product || null,
        leadSource: leadSource || null,
        keyword: keyword.length > 2 ? keyword : "",
        columnFilters: appliedColumnFilters(columnFilters),
        page,
        limit: pageSize,
        sortBy,
        sortDir,
      });
      if (response.data.success) {
        const data = response.data.data || {};
        const nextRows = data.rows || [];
        setRows(nextRows);
        const records = data.totalRecords || 0;
        setTotalRecords(records);
        setTotalPages(data.totalPages || Math.max(1, Math.ceil(records / pageSize)));
        setCurrentPage(data.currentPage || page);
        setSummary(data.summary || null);
        return records;
      }
      setRows([]);
      setTotalRecords(0);
      setSummary(null);
      return 0;
    } catch (error) {
      console.error("Failed to load membership analytics", error);
      setRows([]);
      setTotalRecords(0);
      setSummary(null);
      return 0;
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api("v1/membership-analytics/export", "POST", {
        product: product || null,
        leadSource: leadSource || null,
        keyword: keyword.length > 2 ? keyword : "",
        columnFilters: appliedColumnFilters(columnFilters),
        sortBy,
        sortDir,
      });
      const csv = typeof response.data === "string" ? response.data : "";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "membership-analytics.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setExporting(false);
    }
  };

  reloadRowsRef.current = (page) => fetchRows(page ?? currentPage);

  const waitForRefresh = async (status) => {
    let current = status;
    for (let i = 0; current && current.running && i < REFRESH_POLL_LIMIT; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, REFRESH_POLL_MS));
      if (!mountedRef.current) return null;
      const response = await api("v1/membership-analytics/refresh-status", "GET");
      current = (response.data && response.data.data) || null;
      if (mountedRef.current) setRefreshStatus(current);
    }
    return current;
  };

  // Rebuilds the stored rows from Mongo (no Xero calls) and reloads the table.
  // Page loads reuse a rebuild that finished in the last minute; the button forces one.
  const refreshData = async ({ force = false, page } = {}) => {
    try {
      setRefreshing(true);
      const response = await api("v1/membership-analytics/refresh", "POST", { force });
      if (!response.data.success) return;
      const started = response.data.data || null;
      setRefreshStatus(started);
      if (!started || (!started.running && !force)) return;
      const finished = await waitForRefresh(started);
      if (!mountedRef.current || !finished || finished.running) return;
      if (finished.state === "done") {
        await reloadRowsRef.current(page);
        await fetchLeadSources();
      }
    } catch (error) {
      console.error("Refresh failed", error);
      if (mountedRef.current) setRefreshStatus({ running: false, state: "failed" });
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  };

  const handleRefresh = () => refreshData({ force: true, page: 1 });

  useEffect(() => {
    mountedRef.current = true;
    fetchProducts();
    fetchLeadSources();
    refreshData();
    return () => {
      mountedRef.current = false;
    };
    // Mount-only load. The fetchers close over the first product value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({
          keyword,
          product,
          leadSource,
          columnFilters,
          currentPage,
          sortBy,
          sortDir,
        })
      );
    } catch {
      // sessionStorage may be unavailable
    }
  }, [keyword, product, leadSource, columnFilters, currentPage, sortBy, sortDir]);

  useEffect(() => {
    fetchRows(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    product,
    leadSource,
    columnFilters,
    currentPage,
    sortBy,
    sortDir,
  ]);

  const filtersActive = hasActiveAnalyticsFilters({
    keyword,
    product,
    leadSource,
    columnFilters,
  });

  const setMetricPreset = (id) => (value) => {
    setCurrentPage(1);
    setColumnFilters((current) => ({ ...current, [id]: value }));
  };

  return (
    <div className="page membership-analytics w-full flex flex-column items-center p-4">
      {initialLoad && <GlobalLoadingSpinner />}
      <div className="pageTitle flex flex-col w-full text-center mb-4 gap-3 lg:text-left">
        <h2>Membership analytics</h2>
        <div className="pageActions flex flex-wrap items-center justify-center lg:justify-start gap-3">
          <MetricPresetField
            id="subscription-status"
            label="Subscription status"
            value={columnFilters.paymentStatus}
            options={SUBSCRIPTION_STATUS_PRESETS}
            onChange={setMetricPreset("paymentStatus")}
          />
          <MetricPresetField
            id="months-active"
            label="Months active"
            value={columnFilters.monthsActive}
            options={METRIC_PRESETS.monthsActive}
            onChange={setMetricPreset("monthsActive")}
          />
          <MetricPresetField
            id="redemption-rate"
            label="Redemption rate"
            value={columnFilters.monthlyRedemptionRate}
            options={METRIC_PRESETS.monthlyRedemptionRate}
            onChange={setMetricPreset("monthlyRedemptionRate")}
          />
          <MetricPresetField
            id="monthly-profit"
            label="Monthly profit"
            value={columnFilters.monthlyProfit}
            options={METRIC_PRESETS.monthlyProfit}
            onChange={setMetricPreset("monthlyProfit")}
          />
          <MetricPresetField
            id="account-activation"
            label="Account activation"
            value={columnFilters.accountActivationStatus}
            options={ACTIVATION_PRESETS}
            onChange={setMetricPreset("accountActivationStatus")}
          />
          <MetricPresetField
            id="price-history"
            label="Price history"
            value={columnFilters.priceHistory}
            options={PRICE_HISTORY_PRESETS}
            onChange={setMetricPreset("priceHistory")}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Product</InputLabel>
            <Select
              label="Product"
              value={product}
              onChange={(e) => {
                setCurrentPage(1);
                setProduct(e.target.value);
              }}
            >
              <MenuItem value="">All products</MenuItem>
              {products.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={sourceOptions}
            groupBy={(option) => option.group}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label || ""
            }
            isOptionEqualToValue={(option, value) =>
              (option && option.value) === (value && value.value)
            }
            value={sourceOptions.find((o) => o.value === leadSource) || null}
            onChange={(_e, option) => {
              setCurrentPage(1);
              setLeadSource((option && option.value) || "");
            }}
            renderInput={(params) => <TextField {...params} label="Source" />}
          />
          <TextField
            value={keyword}
            onChange={(e) => {
              setCurrentPage(1);
              setKeyword(e.target.value);
            }}
            variant="outlined"
            size="small"
            sx={{ width: "240px" }}
            placeholder="Email or rego"
          />
          <Button variant="outlined" onClick={handleExport} disabled={exporting || refreshing}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh data"}
          </Button>
        </div>
        {refreshStatusText(refreshStatus) && (
          <Typography
            variant="body2"
            color={refreshStatus.state === "failed" && !refreshStatus.running ? "error" : "text.secondary"}
            data-testid="refresh-status"
          >
            {refreshStatusText(refreshStatus)}
          </Typography>
        )}
      </div>
      <div className="flex w-full">
        <div className="page-content min-h-h-screen flex flex-column justify-start w-full items-center">
          <div className="flex flex-column gap-4 w-full items-center">
            {!initialLoad && rows.length === 0 && !filtersActive ? (
              <EmptyState
                type="merchantAccounts"
                text="No membership analytics yet. Use Refresh data to rebuild the collection."
              />
            ) : (
              !initialLoad && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      alignSelf: "flex-start",
                      maxWidth: "calc(100vw - 200px)",
                      width: "100%",
                      mx: "auto",
                    }}
                  >
                    Rows are stored in the membership analytics collection. Refresh data rebuilds
                    the full set from current memberships. Export CSV downloads every row matching
                    these filters. Use the view icon to open a membership.
                  </Typography>
                  {rows.length > 0 && (
                    <MembershipAnalyticsCharts
                      summary={summary}
                      onSelectSource={(name) => {
                        setCurrentPage(1);
                        setLeadSource(name || NONE_LEAD_SOURCE);
                      }}
                    />
                  )}
                  <MembershipAnalyticsSummary summary={summary} />
                  <MembershipAnalyticsTable
                    rows={rows}
                    priceChanges={priceChanges}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(field, dir) => {
                      setCurrentPage(1);
                      setSortBy(field);
                      setSortDir(dir);
                    }}
                  />
                  {rows.length === 0 && !loading && (
                    <Typography variant="body2" color="text.secondary">
                      No memberships match these filters.
                    </Typography>
                  )}
                  {totalRecords > pageSize && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        disabled={currentPage === 1 || loading}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2">
                        Page {currentPage} of {totalPages} ({totalRecords} total)
                      </Typography>
                      <Button
                        variant="outlined"
                        disabled={currentPage >= totalPages || loading}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipAnalytics;
